/**
 * Solscan utility functions for generating URLs to Solana blockchain explorer
 */

/**
 * Determines if Solscan links should target devnet from the current host.
 * Localhost and devnet deployments use devnet; production tweetonium.xyz uses mainnet.
 */
const isDevnetHost = (): boolean => {
  if (typeof window === 'undefined') return import.meta.env.MODE !== 'production';

  const host = window.location.hostname.toLowerCase();
  if (host === 'tweetonium.xyz' || host === 'www.tweetonium.xyz') return false;
  if (host === 'localhost' || host === '127.0.0.1') return true;
  if (host.includes('devnet.tweetonium.xyz')) return true;

  return import.meta.env.MODE !== 'production';
};

/**
 * Gets the Solscan base URL based on the environment
 * @returns Solscan base URL
 */
const getSolscanBaseUrl = (): string => {
  return 'https://solscan.io';
};

/**
 * Gets the cluster query parameter for Solscan URLs
 * @returns Empty string for mainnet, '?cluster=devnet' for devnet
 */
const getClusterParam = (): string => {
  return isDevnetHost() ? '?cluster=devnet' : '';
};

/**
 * Generates a Solscan URL for a token/NFT mint address
 * @param mintAddress - The token mint address
 * @returns Full Solscan URL for the token
 * @example
 * // Production (mainnet)
 * getSolscanTokenUrl('CRZw6Pm7GkZvHbFQxVZqDzZp8pZKjHxT3YdAo4vw8ZkK')
 * // Returns: 'https://solscan.io/token/CRZw6Pm7GkZvHbFQxVZqDzZp8pZKjHxT3YdAo4vw8ZkK'
 *
 * // Development (devnet)
 * getSolscanTokenUrl('CRZw6Pm7GkZvHbFQxVZqDzZp8pZKjHxT3YdAo4vw8ZkK')
 * // Returns: 'https://solscan.io/token/CRZw6Pm7GkZvHbFQxVZqDzZp8pZKjHxT3YdAo4vw8ZkK?cluster=devnet'
 */
export const getSolscanTokenUrl = (mintAddress: string): string => {
  if (!mintAddress) return '';
  return `${getSolscanBaseUrl()}/token/${mintAddress}${getClusterParam()}`;
};

/**
 * Generates a Solscan URL for a transaction signature
 * @param signature - The transaction signature
 * @returns Full Solscan URL for the transaction
 * @example
 * // Production (mainnet)
 * getSolscanTxUrl('4RDhjML6k7kYaHUtgHiiw54R6dM4pfjq5ujrBYXigiqeRnFRT8pqk3gV37V4x89u6rWkCpueLQGGoXXMZdbaEvAv')
 * // Returns: 'https://solscan.io/tx/4RDhjML6k7kYaHUtgHiiw54R6dM4pfjq5ujrBYXigiqeRnFRT8pqk3gV37V4x89u6rWkCpueLQGGoXXMZdbaEvAv'
 *
 * // Development (devnet)
 * getSolscanTxUrl('4RDhjML6k7kYaHUtgHiiw54R6dM4pfjq5ujrBYXigiqeRnFRT8pqk3gV37V4x89u6rWkCpueLQGGoXXMZdbaEvAv')
 * // Returns: 'https://solscan.io/tx/4RDhjML6k7kYaHUtgHiiw54R6dM4pfjq5ujrBYXigiqeRnFRT8pqk3gV37V4x89u6rWkCpueLQGGoXXMZdbaEvAv?cluster=devnet'
 */
export const getSolscanTxUrl = (signature: string): string => {
  if (!signature) return '';
  return `${getSolscanBaseUrl()}/tx/${signature}${getClusterParam()}`;
};

/**
 * Generates a Solscan URL for an account/wallet address
 * @param address - The account address
 * @returns Full Solscan URL for the account
 * @example
 * getSolscanAccountUrl('CRZw6Pm7GkZvHbFQxVZqDzZp8pZKjHxT3YdAo4vw8ZkK')
 * // Returns: 'https://solscan.io/account/CRZw6Pm7GkZvHbFQxVZqDzZp8pZKjHxT3YdAo4vw8ZkK'
 */
export const getSolscanAccountUrl = (address: string): string => {
  if (!address) return '';
  return `${getSolscanBaseUrl()}/account/${address}${getClusterParam()}`;
};
